/**
 * galeria3/photo-match-cena.js — CASE-105 (a) ekran 6: kwota PRZED podmianą w „Podmień na oryginały".
 *
 * Stawka `photo_match_podmiana` jest dziś zerem, ale operator może ją podnieść w panelu admina
 * (`POST /api/admin/prices`, baza nadpisuje kod) — bez tego modułu klient zapłaciłby wtedy kwotę,
 * której nie widział. Dlatego ekran wysyła nagłówek ceny przy KAŻDYM starcie, także `0.0000`
 * (obietnica zera: gdy stawka wzrośnie między wyceną a kliknięciem, bramka odmawia 409 zamiast pobrać).
 *
 * Bez importów i bez Reacta — `.tools/test-photo-match-cena.mjs` wczytuje ten plik z tekstu.
 * Moduł zamówień (`window.LF_ZAM`, `shared/zamowienie.js`) i pętlę `poKolei` dostaje od wołającego.
 */

const AKCJA = 'photo_match_podmiana';
const POTRZEBNE = ['wycen', 'decyzjaCeny', 'mapaKwot', 'zCena', 'czyCenaZmieniona'];
const ODSWIEZ = 'Odśwież stronę (Cmd+Shift+R) — moduł cen jest nieaktualny. Nic nie zostało wysłane.';

/**
 * Wycena `n` podmian + zgoda klienta → `{start, anulowano, kwoty, suma, tekst, powod}`.
 * `start: false` = zero żądań podmiany (`anulowano: true`, gdy klient odmówił w potwierdzeniu).
 *
 * Pozycja wyceny to SAMA akcja: żądanie podmiany jest multipartem, więc bramka (`_charge_dep`) nie
 * czyta z niego żadnego pola ceny (`request.json()` pada → pusty kontekst). Ten sam kształt po obu
 * stronach = ta sama kwota; dołożenie tu `source_id` wyceniałoby inne żądanie niż pobierane.
 *
 * `potwierdz(tekst)` → bool (np. `window.confirm`) woła się TYLKO przy kwocie > 0. Zero (dziś) nie ma
 * czego pokazać — nagłówek `0.0000` i tak jedzie jako obietnica zera.
 */
export async function przygotujCenePodmiany(zam, n, potwierdz) {
  const stop = (powod, anulowano = false) => ({ start: false, anulowano, kwoty: null, suma: null, tekst: '', powod });
  if (!zam || POTRZEBNE.some((f) => typeof zam[f] !== 'function')) return stop(ODSWIEZ);
  const ile = Number(n);
  if (!Number.isInteger(ile) || ile <= 0) return stop('Nic nie zaznaczono do podmiany.');
  const w = await zam.wycen(Array.from({ length: ile }, () => ({ action_id: AKCJA })));
  const d = zam.decyzjaCeny(w);
  if (!d.start) return stop(d.powod);
  const kwoty = zam.mapaKwot(w, ile);
  if (!kwoty) return stop('Cena podmiany niedostępna — nic nie zostało wysłane. Odśwież stronę i spróbuj ponownie.');
  if (d.kwota > 0 && !(await potwierdz(`Podmienić ${ile} zdj. na Twoje oryginały?\n\n${d.tekst}`))) return stop('', true);
  return { start: true, anulowano: false, kwoty, suma: d.kwota, tekst: d.tekst, powod: '' };
}

/** Błąd HTTP z odpowiedzi `fetch` w kształcie, który rozpoznaje `zam.czyCenaZmieniona` (`status` + `payload`). */
async function bladHttp(r, domyslny) {
  const raw = await r.text().catch(() => '');
  let payload = null;
  try { payload = JSON.parse(raw); } catch { /* nie-JSON */ }
  const d = payload && payload.detail;
  const tekst = typeof d === 'string' && d.trim() ? d
    : (Array.isArray(d) ? d.map((x) => x && x.msg).filter(Boolean).join(' • ') : '')
    || (d && typeof d === 'object' && d.message) || domyslny;
  return Object.assign(new Error(`${tekst} (HTTP ${r.status})`), { status: r.status, payload });
}

/**
 * Wysyłka podmian z kwotą SWOJEJ sztuki w nagłówku. `przygotuj(el)` robi pracę przed wysyłką
 * (zmniejszenie zdjęcia — trwa), `wyslij(dane, naglowki)` zwraca odpowiedź `fetch` (błąd HTTP zamienia
 * się w wyjątek TUTAJ — gołe `new Error(tekst)` u wołającego gubiło `status`; panel na planie).
 *
 * Po 409 `cena_zmieniona` żadna sztuka nie jest WYSYŁANA — klient ma zobaczyć nową kwotę, zanim
 * zapłaci za resztę. Sztuka już w locie kończy się normalnie: niesie własną kwotę, więc bramka porówna
 * ją tak samo (przy zmienionej cenie też dostanie 409, bez pobrania). Inne 409 tej trasy (uszkodzone
 * dane JEDNEGO ogłoszenia) NIE zatrzymują reszty. Sztuka odrzucona odmową ma `cenaZmieniona`.
 */
export async function wyslijPodmiany(lista, kwoty, { przygotuj, wyslij }, poKolei, naraz, naPostep, zam) {
  // Dwa stany, bo rozpoznanie odmowy wymaga odczytu treści (kilka kroków asynchronicznych):
  //  • `wstrzymanie` — stawiane SYNCHRONICZNIE na samym kodzie 409; każda sztuka gotowa do wysłania czeka.
  //    Bez tego drugi pracownik zdążał wysłać kolejną sztukę, zanim treść odmowy została przeczytana
  //    (złapane testem 7 — wyszły 3 z 5);
  //  • `stop` — dopiero gdy odmowa okazała się CENOWA. Wcześniej „każde 409 zatrzymuje" pomijało dobre
  //    zdjęcia po 409 „dane uszkodzone" jednego ogłoszenia (panel na diffie: Sol + Muse).
  // Flaga czytana TUŻ PRZED `wyslij`, po `przygotuj` — sprawdzenie na starcie przepuszczało sztukę, która
  // zmniejszała się, gdy przyszła odmowa (panel na diffie, Sol).
  let stop = false;
  let wstrzymanie = null;
  const pominieta = () => Object.assign(new Error('Pominięte — cena podmiany zmieniła się.'), { pominiete: true });
  const wyniki = await poKolei(lista, naraz, async (el, idx) => {
    if (stop) throw pominieta();
    const kwota = kwoty[idx];
    if (typeof kwota !== 'number' || !isFinite(kwota)) throw new Error('Brak ceny tej podmiany — pominięte.');
    const dane = przygotuj ? await przygotuj(el) : el;
    while (wstrzymanie) await wstrzymanie;
    if (stop) throw pominieta();
    const r = await wyslij(dane, zam.zCena({}, kwota));
    if (r.ok) return r.json();
    let zwolnij = null;
    let moje = null;
    // 409 `cena_zmieniona` i 428 `cena_wymagana` (CASE-058 §11.3 pkt 4) — reszta czeka na odczyt odmowy.
    if (r.status === 409 || r.status === 428) { moje = new Promise((res) => { zwolnij = res; }); wstrzymanie = moje; }
    try {
      const e = await bladHttp(r, 'Podmiana nie powiodła się');
      const c = zam.czyCenaZmieniona(e);
      if (c) { stop = true; throw Object.assign(new Error(c.message), { cenaZmieniona: c }); }
      throw e;
    } finally {
      if (moje) { if (wstrzymanie === moje) wstrzymanie = null; zwolnij(); }
    }
  }, naPostep);
  return wyniki.map((w) => (w.ok || !w.blad || !w.blad.cenaZmieniona) ? w : { ...w, cenaZmieniona: w.blad.cenaZmieniona });
}
